import type { JSX, Ref } from 'react'
import type { ExtOComponent, TAutoplay, TPlayer } from 'video.js'
import type { Events } from '|>/shared/events'
import type { MAX_AUTO_QUALITY } from '|>/shared/types/quality'
import type { SkipSegment } from './components/skip-segment/skip-segment.h'
import type { Configuration } from './core'
import type { PlayerRef, VideojsBoxRef } from './videojs'

import { memo, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePlayerImperativeHandle, VideojsPlayer } from './videojs'

export type PlayerProps = {
  ref?: Ref<PlayerRef>
  using?: ExtOComponent
  className?: string // to be able to add custom classes for external styling (for root div)
  videoClassName?: string // (for video-js elevent)
  sourceUrl?: string
  sourceHeaders?: Record<string, string>
  drm?: any
  contentKey?: string | number
  title?: string
  subtitle?: string
  onPlayNext?: () => void
  onPlayNextEpisode?: () => void
  onPlayPrevious?: () => void
  onCloseClick?: (_?: undefined, event?: any) => void
  onReady?: (instance: TPlayer) => void
  onPlaybackEnded?: (endedContentKey?: string | number) => void
  onProgressReachedEnd?: () => void
  liveProgressBar?: Events.Media.ProgressBarChanged
  onLiveRewind?: (value: number | undefined) => void
  onDelayedLiveRewind?: (value: number | undefined) => void
  onGoLive?: (event?: any) => void
  isRewound?: boolean
  isRewindPending?: boolean
  rewindDetails?: {
    rewoundTargetStartSec: number
    rewoundUrlStartSec: number
    rewindType: string | undefined
  }
  isAutoLiveTracker?: boolean
  onProgramClick?: (event?: any) => void
  onRedirectTvClick?: (event?: any) => void
  onInfoClick?: (event?: any) => void
  onPlaylistClick?: (event?: any) => void
  skipSegments?: SkipSegment[]
  chromecast?: Configuration['chromecast']
  cmcd?: Configuration['cmcd']
  isUrlRequestPending?: boolean
  externalError?: Events.Media.Error
  children?: React.ReactNode
  locale?: string
  muted?: boolean
  autoplay?: TAutoplay
  poster?: Events.Controls.SetPoster
  logo?: string
  seekStep?: number
  onPlaying?: () => void
  onTokenExpired?: () => void
  maxAutoQuality?: MAX_AUTO_QUALITY
  dolby?: Configuration['dolby']
  smartlib?: Configuration['smartlib']
  isSmartlibEnabled?: boolean
  isPlayerDisabled?: boolean
}

/**
 * Player component
 */
export const Player = memo(Player_)
function Player_(props: PlayerProps): JSX.Element {
  const { ref } = props

  // console.log('💥', props)

  // container ref for player
  const containerRef = useRef<HTMLDivElement | null>(null)

  // box contains refs to video tag, player instance and slot, exposed by VideoJS component
  const [box, setBox] = useState<VideojsBoxRef | null>(null)
  const slot = box?.slot()

  const [isRenderedChildren, setHasRenderedChildren] = useState(false)

  // export imperative handle to the parent component
  usePlayerImperativeHandle(ref, box, containerRef)

  const onReady = useCallback(
    (box) => {
      props.onReady?.(box.player())
      setBox(box)
    },
    [props.onReady]
  )

  useEffect(() => {
    if (!slot) return

    const update = () => {
      setHasRenderedChildren(slot.children.length > 0)
    }

    update()
    const observer = new MutationObserver(update)
    observer.observe(slot, { childList: true })

    return () => observer.disconnect()
  }, [slot])

  return (
    <div ref={containerRef} className={`vjs-pureya ${props.className ?? ''}`}>
      <VideojsPlayer
        using={props.using}
        sourceUrl={props.sourceUrl}
        sourceHeaders={props.sourceHeaders}
        drm={props.drm}
        contentKey={props.contentKey}
        title={props.title}
        subtitle={props.subtitle}
        videoClassName={props.videoClassName}
        onPlayNext={props.onPlayNext}
        onPlayNextEpisode={props.onPlayNextEpisode}
        onPlayPrevious={props.onPlayPrevious}
        onClose={props.onCloseClick}
        onPlaybackEnded={props.onPlaybackEnded}
        onProgressReachedEnd={props.onProgressReachedEnd}
        liveProgressBar={props.liveProgressBar}
        onLiveRewind={props.onLiveRewind}
        onDelayedLiveRewind={props.onDelayedLiveRewind}
        onGoLive={props.onGoLive}
        isRewound={props.isRewound}
        isRewindPending={props.isRewindPending}
        rewindDetails={props.rewindDetails}
        isAutoLiveTracker={props.isAutoLiveTracker}
        onProgramClick={props.onProgramClick}
        onRedirectTvClick={props.onRedirectTvClick}
        onInfoClick={props.onInfoClick}
        onPlaylistClick={props.onPlaylistClick}
        skipSegments={props.skipSegments}
        externalError={props.externalError}
        chromecast={props.chromecast}
        cmcd={props.cmcd}
        onReady={onReady}
        isUrlRequestPending={props.isUrlRequestPending}
        locale={props.locale}
        muted={props.muted}
        autoplay={props.autoplay}
        isRenderedChildren={isRenderedChildren}
        poster={props.poster}
        logo={props.logo}
        seekStep={props.seekStep}
        onPlaying={props.onPlaying}
        onTokenExpired={props.onTokenExpired}
        maxAutoQuality={props.maxAutoQuality}
        dolby={props.dolby}
        smartlib={props.smartlib}
        isSmartlibEnabled={props.isSmartlibEnabled}
        isPlayerDisabled={props.isPlayerDisabled}
      />
      {/* render children into provided slot */}
      {slot && props.children && createPortal(props.children, slot)}
    </div>
  )
}
